// dataFilters.js - Corrected version
import moment from 'moment';
import determineStage from '../DetermineStage';
const getStartOfDayTimestamp = (date) => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export const filterMilestoneDates = (key, value, initiative) => {
  const fromDate = value.fromDate ? getStartOfDayTimestamp(value.fromDate) : null;
  const toDate = value.toDate ? getStartOfDayTimestamp(value.toDate) : null;
  const includeWithoutDate = value.includeWithoutDate ?? false;

  if (!fromDate && !toDate) return true;

  if (!initiative.milestones || !Array.isArray(initiative.milestones)) {
    return false;
  }

  return initiative.milestones.some((milestone) => {
    let rawDate = null;

    if (key === 'Work starting date') {
      rawDate = milestone.startDate;
    } else if (key === 'Milestone completion date') {
      rawDate = milestone.endDate;
    }

    if (!rawDate) {
      return includeWithoutDate;
    }

    const milestoneDate = getStartOfDayTimestamp(rawDate);
    if (!milestoneDate) return includeWithoutDate;

    if (fromDate && toDate) {
      return milestoneDate >= fromDate && milestoneDate <= toDate;
    } else if (fromDate) {
      return milestoneDate >= fromDate;
    } else if (toDate) {
      return milestoneDate <= toDate;
    }

    return false;
  });
};

export const filterTimelineDates = (key, value, initiative) => {
  const fromDate = value.fromDate ? getStartOfDayTimestamp(value.fromDate._d || value.fromDate) : null;
  const toDate = value.toDate ? getStartOfDayTimestamp(value.toDate._d || value.toDate) : null;

  if (!fromDate && !toDate) {
    return true;
  }

  const isActualDateFilter = key.includes('actual date');
  const isOriginalPlannedDate = key.includes('Original Planned Date');
  const stageKey = key.split(' ')[0]; // e.g., 'L1'

  const timeLineEntry = initiative.timeline?.find((entry) => entry.stage === stageKey);
  if (!timeLineEntry || !timeLineEntry.date) {
    return false;
  }

  let rawInitiativeDate;
  if (isActualDateFilter) {
    rawInitiativeDate = timeLineEntry.date.actual;
  } else if (isOriginalPlannedDate) {
    rawInitiativeDate = timeLineEntry.date.forecast;
  } else {
    rawInitiativeDate = timeLineEntry.date.plan;
  }

  if (!rawInitiativeDate) {
    return false;
  }

  const initiativeTimestamp = getStartOfDayTimestamp(rawInitiativeDate);
  if (!initiativeTimestamp) {
    return false;
  }

  if (fromDate && toDate) {
    return initiativeTimestamp >= fromDate && initiativeTimestamp <= toDate;
  } else if (fromDate) {
    return initiativeTimestamp >= fromDate;
  } else if (toDate) {
    return initiativeTimestamp <= toDate;
  }

  return false;
};

export const filterApprovalDates = (key, value, initiative, initiativesWithAction) => {
  const fromDate = value?.fromDate ? getStartOfDayTimestamp(value.fromDate._d || value.fromDate) : null;
  const toDate = value?.toDate ? getStartOfDayTimestamp(value.toDate._d || value.toDate) : null;

  // Map filter keys to the expected timeline stage names
  const stageStatusMap = {
    'Sub . for L1 approv . date': 'Submitted for L1 Approval',
    'Sub . for L2 approv . date': 'Submitted for L2 Approval',
    'Sub . for L3 approv . date': 'Submitted for L3 Approval',
    'Sub . for L4 approv . date': 'Submitted for L4 Approval',
    'Sub . for L5 approv . date': 'Submitted for L5 Approval',
    'Sub . back to L3 approv . date': 'Submitted for L3 Approval', // For resubmission
  };

  const expectedStatus = stageStatusMap[key];
  
  if (!expectedStatus) return false;

  // Find the timeline entry for the expected submission stage
  const timelineEntry = initiative.timeline?.find(
    (time) => time.stage === expectedStatus
  );
  
  // Check if the initiative has reached this submission stage
  // The submission stage is considered active if the actual date exists
  const hasReachedSubmissionStage = timelineEntry?.date?.actual !== null && 
                                     timelineEntry?.date?.actual !== undefined;
  
  // If the initiative hasn't reached this submission stage, don't include it
  if (!hasReachedSubmissionStage) {
    return false;
  }

  // If no date range selected, show all initiatives that have reached this submission stage
  if (!fromDate && !toDate) {
    return true;
  }

  // Get the submission date from the timeline
  const submissionDate = timelineEntry?.date?.actual;
  
  if (submissionDate) {
    const submissionTimestamp = new Date(submissionDate).getTime();
    
    if (!isNaN(submissionTimestamp)) {
      if (fromDate && toDate) {
        return submissionTimestamp >= fromDate && submissionTimestamp <= toDate;
      }
      if (fromDate) {
        return submissionTimestamp >= fromDate;
      }
      if (toDate) {
        return submissionTimestamp <= toDate;
      }
    }
  }

  // If we have a date range but no submission date, don't show
  return false;
};

const planChangeKeys = ['annuallyPlan', 'quarterlyPlan', 'monthlyPlan', 'annualizedValues.plan'];
const actualChangeKeys = ['annuallyActual', 'quarterlyActual', 'monthlyActual', 'annualizedValues.actual'];

const hasPlannedUpdate = (changes) => {
  if (!changes) return false;
  return Object.keys(changes).some(key => planChangeKeys.includes(key));
};

const hasActualUpdate = (changes) => {
  if (!changes) return false;
  return Object.keys(changes).some(key => actualChangeKeys.includes(key));
};

export const filterImpactDate = (key, value, initiative) => {
  const fromDate = value?.dateRange?.fromDate ? getStartOfDayTimestamp(value.dateRange.fromDate) : null;
  const toDate = value?.dateRange?.toDate ? getStartOfDayTimestamp(value.dateRange.toDate) : null;

  const history = initiative.history || [];
  const impactUpdateRegex = /^Impact number .+ with (Recurring Negative Impact \(ETB\)|Recurring Benefit \(ETB\)|One Time Benefit) has been updated\.$/;

  const relevantHistory = history.filter(entry => impactUpdateRegex.test(entry.action));
  if (relevantHistory.length === 0) return false;

  const latestHistory = relevantHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  if (!latestHistory) return false;

  const impactDate = new Date(latestHistory.timestamp);
  const impactTimestamp = impactDate.getTime();

  const isWithinRange = () => {
    if (fromDate && toDate) return impactTimestamp >= fromDate && impactTimestamp <= toDate;
    if (fromDate) return impactTimestamp >= fromDate;
    if (toDate) return impactTimestamp <= toDate;
    return true;
  };

  if (key === 'Planned value last update date (calendarized impact)') {
    return hasPlannedUpdate(latestHistory.changes) && isWithinRange();
  }

  if (key === 'Actual value last update date (calendarized impact)') {
    return hasActualUpdate(latestHistory.changes) && isWithinRange();
  }

  return true;
};